import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Webcam from 'react-webcam';

const { FiUpload, FiCamera, FiCheckCircle, FiX, FiAlertCircle, FiUser, FiFileText } = FiIcons;

const KycVerification = () => {
  const navigate = useNavigate();
  const { operatorProfile, uploadKycDocuments } = useOperator();
  const webcamRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [currentDocType, setCurrentDocType] = useState(null);
  
  const [documents, setDocuments] = useState({
    idProof: null,
    addressProof: null,
    profilePhoto: null
  });
  
  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    branch: ''
  });

  useEffect(() => {
    // If KYC is already submitted, redirect to dashboard
    if (operatorProfile && operatorProfile.kyc_submitted) {
      navigate('/operator');
    }
  }, [operatorProfile, navigate]);

  const handleFileChange = (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setDocuments(prev => ({
        ...prev,
        [documentType]: {
          file,
          preview: event.target.result
        }
      }));
    };
    reader.readAsDataURL(file);
  };
  
  const handleCameraCapture = (documentType) => {
    setCurrentDocType(documentType);
    setShowCamera(true);
  };
  
  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error('Failed to capture image');
      return;
    }
    
    // Convert base64 to file
    const byteString = atob(imageSrc.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: 'image/jpeg' });
    const file = new File([blob], `${currentDocType}.jpg`, { type: 'image/jpeg' });
    
    setDocuments(prev => ({
      ...prev,
      [currentDocType]: {
        file,
        preview: imageSrc
      }
    }));
    
    setShowCamera(false);
    toast.success(`${currentDocType} captured successfully`);
  };
  
  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate documents
    if (!documents.idProof || !documents.addressProof || !documents.profilePhoto) {
      toast.error('Please upload all required documents');
      return;
    }
    
    // Validate bank details
    if (!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName || !bankDetails.ifscCode) {
      toast.error('Please fill all bank details');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await uploadKycDocuments(
        documents.idProof.file,
        documents.addressProof.file,
        documents.profilePhoto.file,
        bankDetails
      );
      
      if (result) {
        toast.success('KYC verification submitted successfully!');
        navigate('/operator');
      }
    } catch (error) {
      console.error('Error submitting KYC:', error);
      toast.error('Failed to submit KYC verification');
    } finally {
      setLoading(false);
    }
  };
  
  const removeDocument = (documentType) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: null
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">KYC Verification</h1>
          <p className="text-gray-600 mt-2">
            Complete your verification to start accepting applications
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              <Card className="p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Upload Documents</h2>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        Please ensure that all documents are clear and legible. Maximum file size: 5MB per document.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* ID Proof */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">ID Proof</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload your Aadhaar Card, PAN Card, Driving License, or Passport.
                  </p>
                  
                  {documents.idProof ? (
                    <div className="relative mb-4">
                      <img 
                        src={documents.idProof.preview} 
                        alt="ID Proof" 
                        className="w-full max-h-64 object-contain border rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeDocument('idProof')}
                        className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-4 mb-4">
                      <label className="flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700">
                        <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                        <span>Upload ID Proof</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'idProof')}
                        />
                      </label>
                      
                      <button
                        type="button"
                        onClick={() => handleCameraCapture('idProof')}
                        className="flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <SafeIcon icon={FiCamera} className="w-5 h-5 mr-2" />
                        <span>Capture with Camera</span>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Address Proof */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Address Proof</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload your Utility Bill, Rental Agreement, or any government issued address proof.
                  </p>
                  
                  {documents.addressProof ? (
                    <div className="relative mb-4">
                      <img 
                        src={documents.addressProof.preview} 
                        alt="Address Proof" 
                        className="w-full max-h-64 object-contain border rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeDocument('addressProof')}
                        className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-4 mb-4">
                      <label className="flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700">
                        <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                        <span>Upload Address Proof</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'addressProof')}
                        />
                      </label>
                      
                      <button
                        type="button"
                        onClick={() => handleCameraCapture('addressProof')}
                        className="flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <SafeIcon icon={FiCamera} className="w-5 h-5 mr-2" />
                        <span>Capture with Camera</span>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Profile Photo */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Photo</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a clear, recent passport-sized photograph with a plain background.
                  </p>
                  
                  {documents.profilePhoto ? (
                    <div className="relative w-40 h-40 mx-auto mb-4">
                      <img 
                        src={documents.profilePhoto.preview} 
                        alt="Profile Photo" 
                        className="w-40 h-40 object-cover border rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeDocument('profilePhoto')}
                        className="absolute top-2 right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4 mb-4">
                      <div className="w-40 h-40 bg-gray-100 flex items-center justify-center rounded-lg border border-dashed border-gray-300">
                        <SafeIcon icon={FiUser} className="w-16 h-16 text-gray-300" />
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700">
                          <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                          <span>Upload Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, 'profilePhoto')}
                          />
                        </label>
                        
                        <button
                          type="button"
                          onClick={() => handleCameraCapture('profilePhoto')}
                          className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <SafeIcon icon={FiCamera} className="w-5 h-5 mr-2" />
                          <span>Take Photo</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              <Card className="p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Bank Account Details</h2>
                <p className="text-gray-600 mb-6">
                  Your earnings will be transferred to this account. Please ensure the details are accurate.
                </p>
                
                <div className="space-y-6">
                  <Input
                    label="Account Holder Name"
                    name="accountName"
                    value={bankDetails.accountName}
                    onChange={handleBankDetailsChange}
                    placeholder="Enter account holder name"
                    required
                  />
                  
                  <Input
                    label="Account Number"
                    name="accountNumber"
                    value={bankDetails.accountNumber}
                    onChange={handleBankDetailsChange}
                    placeholder="Enter account number"
                    required
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Bank Name"
                      name="bankName"
                      value={bankDetails.bankName}
                      onChange={handleBankDetailsChange}
                      placeholder="Enter bank name"
                      required
                    />
                    
                    <Input
                      label="IFSC Code"
                      name="ifscCode"
                      value={bankDetails.ifscCode}
                      onChange={handleBankDetailsChange}
                      placeholder="Enter IFSC code"
                      required
                    />
                  </div>
                  
                  <Input
                    label="Branch Name"
                    name="branch"
                    value={bankDetails.branch}
                    onChange={handleBankDetailsChange}
                    placeholder="Enter branch name"
                    required
                  />
                </div>
              </Card>
              
              <div className="flex justify-end">
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full md:w-auto"
                >
                  Submit KYC Verification
                </Button>
              </div>
            </form>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${documents.idProof ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {documents.idProof ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-gray-500">1</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className={`font-medium ${documents.idProof ? 'text-green-600' : 'text-gray-600'}`}>
                      ID Proof
                    </p>
                    <p className="text-xs text-gray-500">
                      {documents.idProof ? 'Uploaded' : 'Required'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${documents.addressProof ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {documents.addressProof ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-gray-500">2</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className={`font-medium ${documents.addressProof ? 'text-green-600' : 'text-gray-600'}`}>
                      Address Proof
                    </p>
                    <p className="text-xs text-gray-500">
                      {documents.addressProof ? 'Uploaded' : 'Required'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${documents.profilePhoto ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {documents.profilePhoto ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-gray-500">3</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className={`font-medium ${documents.profilePhoto ? 'text-green-600' : 'text-gray-600'}`}>
                      Profile Photo
                    </p>
                    <p className="text-xs text-gray-500">
                      {documents.profilePhoto ? 'Uploaded' : 'Required'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      bankDetails.accountName && bankDetails.accountNumber && bankDetails.bankName && bankDetails.ifscCode
                        ? 'bg-green-100'
                        : 'bg-gray-100'
                    }`}>
                      {bankDetails.accountName && bankDetails.accountNumber && bankDetails.bankName && bankDetails.ifscCode ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-gray-500">4</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className={`font-medium ${
                      bankDetails.accountName && bankDetails.accountNumber && bankDetails.bankName && bankDetails.ifscCode
                        ? 'text-green-600'
                        : 'text-gray-600'
                    }`}>
                      Bank Details
                    </p>
                    <p className="text-xs text-gray-500">
                      {bankDetails.accountName && bankDetails.accountNumber && bankDetails.bankName && bankDetails.ifscCode
                        ? 'Completed'
                        : 'Required'}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Guidelines</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Documents should be clearly visible and not blurry</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">All details on the documents should be legible</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Profile photo should show your face clearly</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Bank details should match the name on your ID proof</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Verification usually takes 24-48 hours</span>
                </li>
              </ul>
            </Card>
            
            <Card className="p-6 bg-blue-50">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
                <div>
                  <h4 className="font-medium text-gray-900">Privacy Guarantee</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Your documents are securely handled with our zero data sharing policy. All uploads are encrypted and never shared with third parties.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-4 max-w-lg w-full"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {currentDocType === 'profilePhoto' ? 'Take Profile Photo' : `Capture ${currentDocType === 'idProof' ? 'ID Proof' : 'Address Proof'}`}
              </h3>
              <button
                onClick={() => setShowCamera(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <div className="bg-black rounded-lg overflow-hidden">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full"
                videoConstraints={
                  currentDocType === 'profilePhoto'
                    ? { width: 400, height: 400, facingMode: "user" }
                    : { width: 640, height: 480, facingMode: "environment" }
                }
              />
            </div>
            <div className="flex justify-center mt-4">
              <Button onClick={capturePhoto} className="flex items-center">
                <SafeIcon icon={FiCamera} className="w-5 h-5 mr-2" />
                Capture
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              {currentDocType === 'profilePhoto'
                ? 'Make sure your face is clearly visible and well-lit'
                : 'Make sure the document is clearly visible and all text is legible'}
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default KycVerification;